import { clamp } from '../../shared/scalar';
import { lookup } from '../content';
import { playerById } from '../damage';
import { drawRelics } from '../draw';
import { recruitFallen } from '../recruits';
import { SPECIALS } from '../specials';
import type { EnemyState, PickupKind, SimState } from '../state';
import { dropBomb } from './bomb';
import { dropDie } from './dice';
import { dropBossTrap, dropCarriedTrap } from './carriers';
import { tallyOf } from '../tally';
import { isLastTier } from './set-progress';
import type { StepContext } from './types';

const DROP_SPACING = 40;

export function deaths(ctx: StepContext): void {
  const { state, content, set } = ctx;
  let kept = 0;
  for (const enemy of state.enemies) {
    if (enemy.hp > 0) {
      state.enemies[kept] = enemy;
      kept += 1;
      continue;
    }
    if (enemy.escaped === true) {
      state.events.push({
        type: 'enemyFled',
        id: enemy.id,
        kind: enemy.kind,
        x: enemy.x,
        y: enemy.y,
      });
      continue;
    }
    const definition = lookup(content.enemies, enemy.kind, 'enemy');
    if (definition.special?.kind === 'revive') {
      SPECIALS.revive(ctx, enemy, definition.special);
      if (enemy.hp > 0 || enemy.downTicks !== undefined) {
        state.enemies[kept] = enemy;
        kept += 1;
        continue;
      }
    }
    const base = enemy.isBoss ? set.tiers[state.set.tier]?.boss.vibesDrop : undefined;
    const vibesDrop = (base ?? definition.vibesDrop) + (enemy.carrying ?? 0);
    state.events.push({
      type: 'enemyDied',
      id: enemy.id,
      kind: enemy.kind,
      x: enemy.x,
      y: enemy.y,
      byPlayer: enemy.lastHitBy ?? null,
    });
    state.stats.kills += 1;
    const killer = enemy.lastHitBy === undefined ? undefined : playerById(state, enemy.lastHitBy);
    if (killer !== undefined) {
      const tally = tallyOf(killer);
      tally.badVibes += 1;
      if (enemy.isBoss) {
        tally.bosses += 1;
      }
    }
    if (enemy.called === true) {
      state.stats.calledDown = (state.stats.calledDown ?? 0) + 1;
    }
    recruitFallen(ctx, enemy, definition);
    if (enemy.isBoss && !isLastTier(state.set, set)) {
      offerRelics(ctx);
    }
    dropCarriedTrap(ctx, enemy);
    if (enemy.carriesBomb === true) {
      dropBomb(ctx, enemy.x, enemy.y);
    }
    if (enemy.isBoss) {
      dropBossRows(ctx, enemy);
    }
    if (vibesDrop > 0) {
      drop(state, 'vibes', vibesDrop, enemy.x, enemy.y, set.pickups.lifetimeTicks);
    }
  }
  state.enemies.length = kept;
}

// A placed trap and a die per player, in two rows under the boss, kept whole inside the arena.
function dropBossRows(ctx: StepContext, boss: EnemyState): void {
  const { width, height } = ctx.state.arena;
  const players = ctx.state.players;
  const half = ((players.length - 1) / 2) * DROP_SPACING;
  const margin = DROP_SPACING / 2;
  const left = clamp(boss.x - half, margin, width - margin - 2 * half);
  const y = clamp(boss.y, margin, height - margin);
  const dieY = y + DROP_SPACING <= height - margin ? y + DROP_SPACING : y - DROP_SPACING;
  players.forEach((player, index) => {
    const x = left + index * DROP_SPACING;
    dropBossTrap(ctx, player.id, { x, y });
    dropDie(ctx, x, dieY, boss);
  });
}

function offerRelics({ state, content }: StepContext): void {
  for (const player of state.players) {
    if (player.gone === true) {
      continue;
    }
    const options = drawRelics(state.rng, state, content, player);
    if (options.length > 0) {
      const rarities = options.map(() => 'common' as const);
      state.pendingUpgrades.push({ playerId: player.id, options, rarities, kind: 'relic' });
      state.events.push({ type: 'relicOffered', playerId: player.id, options });
    }
  }
}

function drop(
  state: SimState,
  kind: PickupKind,
  amount: number,
  x: number,
  y: number,
  ticksLeft: number,
): void {
  state.pickups.push({
    id: state.nextEntityId,
    kind,
    amount,
    x,
    y,
    prevX: x,
    prevY: y,
    ticksLeft,
  });
  state.nextEntityId += 1;
}
